import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Button, Spin } from 'antd';
import queryString from 'query-string';
import { useDidUpdate } from 'rooks';
import { isNil } from 'lodash';
import cx from 'classnames';
import {
  Bar,
  XAxis,
  YAxis,
  Text,
  Legend,
  Tooltip,
  BarChart,
  LabelList,
  Rectangle,
  LabelProps,
  LegendProps,
  TooltipProps,
  LegendPayload,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';

import {
  getDateRangeTillLastSunday,
  getDateWithoutTime,
} from 'utils/dateUtils';
import ChartDateRangePicker, {
  DateRangeFilterType,
} from '../ChartDateRangePicker';
import {
  BillingAndProfitSortFieldEnum,
  SortingEnum,
  SummaryBillingAndProfitReportViewModel,
} from 'api/backofficeApiClient';
import { DateParams } from 'utils/filterUtils';
import { getFormattedNumber } from 'utils/numberUtils';
import { changeOtherChartItemIndex, dateRangeDurationInWeeks } from '../utils';
import { getCustomerBillingProfitChartData } from 'redux/reducers/homeDashboardSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from './CustomerBillingProfitChart.module.less';

enum CustomerBillingProfitBarChartTypes {
  Billing = 'billing',
  MemberOneProfit = 'profit',
}

interface ChartSettings {
  activeBarIndex?: number;
  tooltipVisible: boolean;
  activeLegend?: CustomerBillingProfitBarChartTypes;
}

const chartPageSize = 5;

const renderYAxisTick = (props: any) => {
  const { x, y, width, height, payload } = props;

  return (
    <Text
      x={x}
      y={y}
      width={width}
      height={height}
      textAnchor="end"
      verticalAnchor="middle"
      fill="#000000"
      opacity="0.25"
    >
      {!isNil(payload.value)
        ? `$${getFormattedNumber(Math.round(payload.value))}`
        : ''}
    </Text>
  );
};

const renderTooltip = (props: TooltipProps) => {
  if (!props.active || !props.payload) return null;

  const chartItem: SummaryBillingAndProfitReportViewModel =
    props.payload[1].payload;

  if (isNil(chartItem.profitPercentage)) return '';

  return (
    <div className={styles.tooltip}>
      <span className={styles.colorBox} />
      <span>Profit: {chartItem.profitPercentage.toFixed(2)}%</span>
    </div>
  );
};

const renderShape = (props: any, active: boolean) => {
  const { fill, x, y, width, height, radius } = props;

  return (
    <Rectangle
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
      filter={active ? 'url(#customerBillingProfitChartShadow)' : undefined}
      radius={radius}
      stroke="#000000"
      strokeWidth={active ? '0.05' : '0'}
    />
  );
};

const renderXAxisTick = (
  props: any,
  data: SummaryBillingAndProfitReportViewModel[],
) => {
  const { x, y, width, visibleTicksCount, height, index, payload } = props;
  const dataItem = data[index];

  return (
    <g>
      <foreignObject
        x={x - payload.offset}
        y={y}
        width={width / visibleTicksCount}
        height={height}
      >
        <div className={styles.caption}>{dataItem.customerName || ''}</div>
      </foreignObject>
    </g>
  );
};

const legendFormatter = (entry: any) => {
  // dataKey property is absent in LegendPayload typings
  switch (entry?.dataKey) {
    case CustomerBillingProfitBarChartTypes.Billing:
      return (
        <>
          <span
            className={cx(styles.legendDot, styles.legendDotBilling)}
          ></span>
          <span>Billing $</span>
        </>
      );

    case CustomerBillingProfitBarChartTypes.MemberOneProfit:
      return (
        <>
          <span className={cx(styles.legendDot, styles.legendDotProfit)}></span>
          <span>Member 1 Profit $</span>
        </>
      );

    default:
      return '';
  }
};

const renderBarLabel = (props: LabelProps) => {
  const { x = 0, y = 0, width = 0, height = 0, offset = 0, value } = props;

  return (
    <Text
      x={x + width / 2}
      y={y - offset}
      width={width}
      height={height}
      textAnchor="middle"
      fill="#000000"
      opacity="0.85"
    >
      {!isNil(value)
        ? `$${getFormattedNumber(Math.round(value as number))}`
        : ''}
    </Text>
  );
};

const getFormattedChartData = (
  chartData: SummaryBillingAndProfitReportViewModel[],
): SummaryBillingAndProfitReportViewModel[] => {
  return changeOtherChartItemIndex(chartData, 'customerName');
};

export default function CustomerBillingProfitChart() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const {
    billingProfitStartDate: startDate,
    billingProfitEndDate: endDate,
    ...restQueryParams
  } = queryString.parse(location.search);
  const defaultDateFilter: DateParams = getDateRangeTillLastSunday(
    dateRangeDurationInWeeks,
  );
  const initialDateFilter: DateRangeFilterType =
    startDate && endDate
      ? {
          startDate: moment(startDate, 'MM/DD/YYYY'),
          endDate: moment(endDate, 'MM/DD/YYYY'),
        }
      : defaultDateFilter;
  const [dateFilter, setDateFilter] =
    useState<DateRangeFilterType>(initialDateFilter);
  const [chartData, setChartData] = useState<
    SummaryBillingAndProfitReportViewModel[]
  >([]);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [isInitiaLoaded, setIsInitiaLoaded] = useState(false);
  const [chartSettings, setChartSettings] = useState<ChartSettings>({
    activeBarIndex: undefined,
    tooltipVisible: false,
    activeLegend: undefined,
  });
  const formattedChartData = useMemo(
    () => getFormattedChartData(chartData),
    [chartData],
  );
  const isDisabled = useMemo(
    () =>
      formattedChartData.every(
        (chartItem) =>
          !chartItem.profit &&
          !chartItem.billing &&
          !chartItem.profitPercentage,
      ),
    [formattedChartData],
  );

  useDidUpdate(() => {
    const queryData = {
      billingProfitStartDate: dateFilter?.startDate
        ? dateFilter.startDate.format('MM/DD/YYYY')
        : undefined,
      billingProfitEndDate: dateFilter?.endDate
        ? dateFilter.endDate.format('MM/DD/YYYY')
        : undefined,
      ...restQueryParams,
    };

    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
    // eslint-disable-next-line
  }, [dateFilter]);

  const loadCustomerBillingProfitChartData = useCallback(async () => {
    setIsChartLoading(true);

    const result = await dispatch(
      getCustomerBillingProfitChartData({
        pageSize: chartPageSize,
        sortType: SortingEnum.Desc,
        sortField: BillingAndProfitSortFieldEnum.Billing,
        startDate: dateFilter?.startDate
          ? getDateWithoutTime(dateFilter.startDate)
          : undefined,
        endDate: dateFilter?.endDate
          ? getDateWithoutTime(dateFilter.endDate)
          : undefined,
      }),
    );

    if (getCustomerBillingProfitChartData.fulfilled.match(result)) {
      setChartData(result.payload || []);
    } else {
      setChartData([]);
    }

    setIsChartLoading(false);
    setIsInitiaLoaded(true);
  }, [dispatch, dateFilter]);

  useEffect(() => {
    loadCustomerBillingProfitChartData();
  }, [loadCustomerBillingProfitChartData]);

  const renderLegend = (props: LegendProps) => {
    const { payload } = props;

    if (!payload) return '';

    return (
      <ul className={styles.legendContainer}>
        {payload.map((entry: LegendPayload, index: number) => (
          <li
            key={`customer-billing-profit-legend-${index}`}
            onMouseEnter={() => handleLegendMouseEnter(entry)}
            onMouseLeave={() => handleLegendMouseLeave(entry)}
          >
            {legendFormatter(entry)}
          </li>
        ))}
      </ul>
    );
  };

  const isShapeActive = (
    props: any,
    type: CustomerBillingProfitBarChartTypes,
  ) => {
    return (
      chartSettings.activeBarIndex === props.index ||
      chartSettings.activeLegend === type
    );
  };

  const handleCalendarChange = () => {
    setChartData([]);
  };

  const handleLegendMouseEnter = (event: any) => {
    if (isDisabled) return;

    const { dataKey } = event;

    setChartSettings((settings) => ({
      ...settings,
      activeLegend: dataKey,
    }));
  };

  const handleLegendMouseLeave = (event: any) => {
    setChartSettings((settings) => ({
      ...settings,
      activeLegend: undefined,
    }));
  };

  const handleBarMouseEnter = (
    type: CustomerBillingProfitBarChartTypes,
    index: number,
  ) => {
    if (isDisabled) return;

    const isTooltipVisible =
      type === CustomerBillingProfitBarChartTypes.MemberOneProfit;

    setChartSettings((settings) => ({
      ...settings,
      activeBarIndex: index,
      tooltipVisible: isTooltipVisible,
    }));
  };

  const handleBarMouseLeave = () => {
    setChartSettings((settings) => ({
      ...settings,
      activeBarIndex: undefined,
      tooltipVisible: false,
    }));
  };

  return (
    <div className={cx({ [styles.disabled]: isDisabled }, styles.chartWrapper)}>
      <div className={styles.title}>
        <div>
          <Link to="/customer-billing-profit-report-details">
            Billing & Profit by Customer
          </Link>
          <Button
            type="primary"
            onClick={() => navigate('/customer-billing-profit-report-details')}
          >
            View Details
          </Button>
        </div>

        <div>
          <ChartDateRangePicker
            pickerKey="customerBillingProfitFilterByDate"
            value={dateFilter}
            onChange={setDateFilter}
            onCalendarChange={handleCalendarChange}
          />
        </div>
      </div>

      <Spin spinning={isChartLoading || !isInitiaLoaded}>
        <ResponsiveContainer width="100%" height={420}>
          <BarChart
            data={formattedChartData}
            margin={{
              top: 80,
            }}
            barSize={34}
            barGap={20}
          >
            <defs>
              <linearGradient
                id="customerBillingBarGradient"
                x1="0"
                y1="0"
                x2="0"
                y2="100%"
                spreadMethod="reflect"
              >
                <stop offset="0" stopColor="#FFD78A" />
                <stop offset="1" stopColor="#FFE7BA" />
              </linearGradient>

              <linearGradient
                id="customerProfitBarGradient"
                x1="0"
                y1="0"
                x2="0"
                y2="100%"
                spreadMethod="reflect"
              >
                <stop offset="0" stopColor="#65BB6A" />
                <stop offset="1" stopColor="#A5D6A7" />
              </linearGradient>

              <filter
                id="customerBillingProfitChartShadow"
                x="0"
                y="0"
                width="150%"
                height="150%"
                filterUnits="userSpaceOnUse"
              >
                <feDropShadow
                  dx="0"
                  dy="0"
                  stdDeviation="3"
                  floodColor="#141e24"
                />
              </filter>
            </defs>

            {!isDisabled && chartSettings.tooltipVisible && (
              <Tooltip
                cursor={false}
                animationDuration={400}
                content={renderTooltip}
              />
            )}

            {isInitiaLoaded && (
              <Legend
                height={36}
                align="left"
                iconSize={0}
                verticalAlign="top"
                content={renderLegend}
                wrapperStyle={{
                  top: 20,
                }}
              />
            )}

            <CartesianGrid stroke="#f5f5f5" vertical={false} />

            <YAxis
              width={80}
              axisLine={false}
              tickLine={false}
              tick={renderYAxisTick}
              domain={[0, isDisabled ? 1000 : 'auto']}
            />

            <Bar
              minPointSize={3}
              dataKey={CustomerBillingProfitBarChartTypes.Billing}
              fill={'url(#customerBillingBarGradient)'}
              radius={[10, 10, 0, 0]}
              shape={(props: any) =>
                renderShape(
                  props,
                  isShapeActive(
                    props,
                    CustomerBillingProfitBarChartTypes.Billing,
                  ),
                )
              }
              onMouseEnter={(data: any, index: number) =>
                handleBarMouseEnter(
                  CustomerBillingProfitBarChartTypes.Billing,
                  index,
                )
              }
              onMouseLeave={handleBarMouseLeave}
            >
              <LabelList
                position="top"
                content={renderBarLabel}
                dataKey={CustomerBillingProfitBarChartTypes.Billing}
              />
            </Bar>

            <Bar
              minPointSize={3}
              dataKey={CustomerBillingProfitBarChartTypes.MemberOneProfit}
              fill={'url(#customerProfitBarGradient)'}
              radius={[10, 10, 0, 0]}
              shape={(props: any) =>
                renderShape(
                  props,
                  isShapeActive(
                    props,
                    CustomerBillingProfitBarChartTypes.MemberOneProfit,
                  ),
                )
              }
              onMouseEnter={(data: any, index: number) =>
                handleBarMouseEnter(
                  CustomerBillingProfitBarChartTypes.MemberOneProfit,
                  index,
                )
              }
              onMouseLeave={handleBarMouseLeave}
            >
              <LabelList
                position="top"
                content={renderBarLabel}
                dataKey={CustomerBillingProfitBarChartTypes.MemberOneProfit}
              />
            </Bar>

            <XAxis
              interval={0}
              height={44}
              axisLine={false}
              tickLine={false}
              tick={(props) => renderXAxisTick(props, formattedChartData)}
            />
          </BarChart>
        </ResponsiveContainer>
      </Spin>
    </div>
  );
}
